import { UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Copy, ExternalLink, GitCommitHorizontal, Loader2, Rocket, Sparkles, User } from "lucide-react";

import { cn } from "@/libs/cn";
import Button from "@/modules/common/components/files/Button";
import CheckboxV2 from "@/modules/common/components/files/CheckboxV2";
import { Modal, ModalBody, ModalFooter } from "@/modules/common/components/files/Modal";
import Spinner from "@/modules/common/components/files/Spinner";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { formatDate } from "@/modules/common/utils/format";
import { ADMIN_RELEASE_NOTES_ENDPOINTS, AdminReleaseNotesService } from "@/service/api/admin-release-notes";
import { ReleaseNoteGitHubCommit } from "@/service/response/admin-release-notes";

import { parseChatAnswer, ReleaseNoteDraft } from "../chat-answer";
import { buildReleaseNotePrompt } from "../prompt";

const PAGE_SIZE = 30;
const LOAD_MORE_DISTANCE_PX = 200;

const textareaClassName =
  "w-full resize-y rounded-lg border border-ui-border bg-white px-3 py-2 font-mono text-xs leading-5 text-ui-primary focus:outline-none focus:ring-2 focus:ring-ui-primary/30";

const changeKey = (change: ReleaseNoteGitHubCommit) => `${change.repository}@${change.sha}`;

const isNearBottom = (element: HTMLElement) => element.scrollHeight - element.scrollTop - element.clientHeight < LOAD_MORE_DISTANCE_PX;

const REPOSITORY_LABELS: [suffix: string, label: string][] = [
  ["backend", "Back"],
  ["manager", "Manager"],
  ["client", "Client"],
  ["admin", "Admin"],
];

const repositoryLabel = (repository: string) => {
  const name = repository.split("/").pop() ?? repository;
  return REPOSITORY_LABELS.find(([suffix]) => name.endsWith(suffix))?.[1] ?? name;
};

const repositoryOrder = (repository: string) => {
  const index = REPOSITORY_LABELS.findIndex(([suffix]) => repository.endsWith(suffix));
  return index === -1 ? REPOSITORY_LABELS.length : index;
};

const promotionClassName = (targetBranch: string) => {
  if (targetBranch === "production") return "bg-[#EEF6DA] text-[#4D7C0F]";
  if (targetBranch === "staging") return "bg-[#FEF3C7] text-[#B45309]";
  return "bg-[#EEF2FF] text-[#3730A3]";
};

type Step = "select" | "prompt";

interface GenerateReleaseNoteModalProps {
  open: boolean;
  hasContent: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated: (draft: ReleaseNoteDraft) => void;
}

const GenerateReleaseNoteModal = ({ open, hasContent, onOpenChange, onGenerated }: GenerateReleaseNoteModalProps) => {
  const [step, setStep] = useState<Step>("select");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [copied, setCopied] = useState(false);
  const [answer, setAnswer] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const query = useInfiniteQuery({
    queryKey: [ADMIN_RELEASE_NOTES_ENDPOINTS.GITHUB_COMMITS],
    queryFn: ({ pageParam }) => AdminReleaseNotesService.listGitHubCommits({ cursor: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: open,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const { changes, lastPageAddedNothing } = useMemo(() => {
    const byKey = new Map<string, ReleaseNoteGitHubCommit>();
    let addedByLastPage = 0;

    query.data?.pages.forEach((page, index) => {
      const before = byKey.size;
      page.items.forEach((item) => byKey.set(changeKey(item), item));
      if (index === query.data!.pages.length - 1) addedByLastPage = byKey.size - before;
    });

    return { changes: [...byKey.values()], lastPageAddedNothing: Boolean(query.data?.pages.length) && addedByLastPage === 0 };
  }, [query.data]);

  const lastDeploys = useMemo(
    () =>
      [...(query.data?.pages[0]?.lastDeploys ?? [])].sort(
        (left, right) => repositoryOrder(left.repository) - repositoryOrder(right.repository),
      ),
    [query.data],
  );
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const canLoadMore = Boolean(hasNextPage) && !isFetchingNextPage && !query.isError;

  useEffect(() => {
    if (query.isError) toast.error(getApiErrorMessage(query.error, "Não foi possível buscar os commits de produção agora."));
  }, [query.isError, query.error]);

  useEffect(() => {
    const list = listRef.current;
    if (step !== "select" || !list || !canLoadMore) return;
    if (lastPageAddedNothing || isNearBottom(list)) void fetchNextPage();
  }, [step, canLoadMore, lastPageAddedNothing, changes.length, fetchNextPage]);

  const loadMoreOnScroll = (event: UIEvent<HTMLDivElement>) => {
    if (canLoadMore && isNearBottom(event.currentTarget)) void fetchNextPage();
  };

  const selectedChanges = changes.filter((change) => selected.has(changeKey(change)));
  const prompt = useMemo(() => (step === "prompt" ? buildReleaseNotePrompt(selectedChanges) : ""), [step, selectedChanges]);

  const close = () => {
    onOpenChange(false);
    setStep("select");
    setSelected(new Set());
    setAnswer("");
    setCopied(false);
  };

  const toggle = (key: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch {
      toast.error("Não foi possível copiar. Selecione o texto e copie manualmente.");
    }
  };

  const useAnswer = () => {
    const draft = parseChatAnswer(answer);

    if (!draft.content) {
      toast.error("Cole a resposta completa da IA, com o texto da nota.");
      return;
    }

    onGenerated(draft);
    toast.success(
      draft.title ? "Texto aplicado no editor. Revise antes de salvar." : "Texto aplicado no editor. Preencha o título antes de salvar.",
    );
    close();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={step === "select" ? "Atualizações enviadas para produção" : "Gerar conteúdo com IA"}
      description={
        step === "select"
          ? "Selecione as atualizações que devem entrar na nota. A IA usa apenas as atualizações marcadas."
          : "Use o prompt numa IA de chat e traga a resposta de volta."
      }
      className={cn("my-4 flex max-h-[calc(100dvh-2rem)] flex-col p-5 sm:p-8", step === "select" ? "max-w-3xl" : "max-w-6xl")}
    >
      {step === "select" && (
        <>
          <ModalBody className="min-h-0 flex-1 gap-3">
            {lastDeploys.length > 0 && (
              <div className="rounded-xl border border-ui-border bg-[#F8FAFC] p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium tracking-wide text-ui-text-muted uppercase">
                  <Rocket size={13} />
                  Último deploy
                </p>
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {lastDeploys.map(({ repository, deployedAt }) => (
                    <div key={repository} className="min-w-0 rounded-lg bg-white px-3 py-2 shadow-xs">
                      <dt className="truncate text-xs text-ui-text-muted">{repositoryLabel(repository)}</dt>
                      <dd className="text-sm font-semibold text-ui-primary tabular-nums">
                        {deployedAt ? formatDate(deployedAt, "DD/MM/YYYY HH:mm") : "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div ref={listRef} onScroll={loadMoreOnScroll} className="flex min-h-40 flex-1 flex-col gap-2 overflow-y-auto pr-1">
              {query.isLoading ? (
                <div className="flex min-h-40 items-center justify-center">
                  <Spinner />
                </div>
              ) : query.isError ? (
                <p className="py-10 text-center text-sm text-ui-text-muted">Não foi possível carregar os commits.</p>
              ) : !changes.length ? (
                <p className="py-10 text-center text-sm text-ui-text-muted">
                  Nenhuma atualização (dev → main, main → staging ou staging → production) encontrada.
                </p>
              ) : (
                changes.map((change) => {
                  const key = changeKey(change);
                  const checked = selected.has(key);

                  return (
                    <label
                      key={key}
                      htmlFor={key}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition",
                        checked ? "border-[#B5D66A] bg-[#F7FBEA]" : "border-ui-border bg-white hover:bg-gray-50",
                      )}
                    >
                      <CheckboxV2 id={key} className="mt-1" checked={checked} onCheckedChange={(value) => toggle(key, value === true)} />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-ui-primary">
                            {repositoryLabel(change.repository)}
                          </span>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-xs font-medium",
                              promotionClassName(change.targetBranch),
                            )}
                          >
                            {change.sourceBranch}
                            <ArrowRight size={11} />
                            {change.targetBranch}
                          </span>
                          <span className="font-mono text-xs text-ui-text-muted">{change.sha.slice(0, 7)}</span>
                        </span>
                        <span className="mt-1 block text-sm font-medium text-ui-primary">{change.title}</span>
                        {change.description && (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-ui-text-muted">{change.description}</span>
                        )}
                        <span className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-ui-text-muted">
                          <User size={12} />
                          {change.author || "—"} · {formatDate(change.mergedAt, "DD/MM/YYYY HH:mm")}
                          <a
                            href={`https://github.com/${change.repository}/pull/${change.pullRequest}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            className="ml-2 inline-flex items-center gap-1 hover:text-ui-primary"
                          >
                            #{change.pullRequest}
                            <ExternalLink size={11} />
                          </a>
                        </span>
                      </span>
                    </label>
                  );
                })
              )}

              {isFetchingNextPage && (
                <>
                  {[0, 1].map((index) => (
                    <div key={index} className="flex animate-pulse items-start gap-3 rounded-xl border border-ui-border bg-white px-4 py-3">
                      <div className="mt-1 size-5 shrink-0 rounded bg-gray-100" />
                      <div className="flex flex-1 flex-col gap-2">
                        <div className="h-4 w-40 rounded bg-gray-100" />
                        <div className="h-4 w-3/4 rounded bg-gray-100" />
                        <div className="h-3 w-48 rounded bg-gray-100" />
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div className="flex shrink-0 items-center justify-center gap-2 px-2 pt-2 pb-3 text-xs text-ui-text-muted">
                {isFetchingNextPage ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Carregando mais atualizações…
                  </>
                ) : canLoadMore && changes.length > 0 ? (
                  <Button
                    type="button"
                    variant="text"
                    color="primary"
                    size="small"
                    label="Carregar mais"
                    onClick={() => void fetchNextPage()}
                  />
                ) : (
                  changes.length > 0 && !hasNextPage && "Início da branch — não há atualizações mais antigas."
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-ui-text-muted">
              <strong className="text-ui-primary">{selectedChanges.length}</strong>{" "}
              {selectedChanges.length === 1 ? "atualização selecionada" : "atualizações selecionadas"}
            </span>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button type="button" variant="outlined" color="primary" label="Cancelar" onClick={close} />
              <Button
                type="button"
                color="primary"
                icon={<Sparkles size={16} />}
                label="Gerar conteúdo"
                disabled={!selectedChanges.length}
                onClick={() => {
                  setCopied(false);
                  setStep("prompt");
                }}
              />
            </div>
          </ModalFooter>
        </>
      )}

      {step === "prompt" && (
        <>
          <ModalBody className="grid min-h-0 flex-1 gap-6 overflow-y-auto lg:grid-cols-2">
            <section className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-ui-primary">1. Copie o prompt</h3>
                <span className="inline-flex items-center gap-1 text-xs text-ui-text-muted">
                  <GitCommitHorizontal size={13} />
                  {selectedChanges.length} {selectedChanges.length === 1 ? "atualização" : "atualizações"}
                </span>
              </div>
              <textarea
                readOnly
                value={prompt}
                rows={18}
                className={cn(textareaClassName, "flex-1")}
                onFocus={(event) => event.target.select()}
              />
              <div>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  size="small"
                  icon={copied ? <Check size={15} /> : <Copy size={15} />}
                  label={copied ? "Copiado" : "Copiar prompt"}
                  onClick={copyPrompt}
                />
              </div>
            </section>

            <div className="flex min-w-0 flex-col gap-4">
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-ui-primary">2. Cole numa IA de chat</h3>
                <p className="text-sm text-ui-text-muted">
                  Abra o{" "}
                  <a href="https://claude.ai/new" target="_blank" rel="noreferrer" className="text-brand-primary underline">
                    Claude
                  </a>{" "}
                  ou o{" "}
                  <a href="https://gemini.google.com/app" target="_blank" rel="noreferrer" className="text-brand-primary underline">
                    Gemini
                  </a>
                  , cole o prompt e envie. Copie a resposta inteira.
                </p>
              </section>

              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-ui-primary">3. Cole a resposta aqui</h3>
                <textarea
                  value={answer}
                  rows={14}
                  placeholder={"# Título da nota\n\nIntrodução...\n\n## Módulo atualizado\n\nO que mudou e como melhorou..."}
                  className={textareaClassName}
                  onChange={(event) => setAnswer(event.target.value)}
                />
                {hasContent && <p className="text-sm text-[#B45309]">O título e o conteúdo atuais serão substituídos pela resposta.</p>}
              </section>
            </div>
          </ModalBody>
          <ModalFooter className="shrink-0">
            <Button
              type="button"
              variant="outlined"
              color="primary"
              icon={<ArrowLeft size={16} />}
              label="Voltar às atualizações"
              onClick={() => setStep("select")}
            />
            <Button type="button" color="success" label="Usar no editor" disabled={!answer.trim()} onClick={useAnswer} />
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default GenerateReleaseNoteModal;
